import {
  useWSSnackbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { buildPayeeEditTaxInfoPath } from "../../utils";
import { useCreatePayeeEngagementModal } from "../../components/CreatePayeeEngagementModal/useCreateEngagementModal";
import { useEditContractorModal } from "../../components/UpdateContractorModal/useUpdateContractorModal";
import { useInternalNotesModal } from "../../components/InternalNotesModal/useInternalNotesModal";
import { useArchiveContractorModal } from "../../components/ArchiveContractorModal";
import { queriesPayees } from "@query";
import { useXWingspanPayerId } from "./useXWingspanPayerId";
import { useGoTo } from "../../../../shared/utils/useGoTo";
import { useHistory } from "react-router-dom";
import { useAccountAccess } from "../../../../v3/hooks/useAccountAccess";
import { useResendPayeeInvite } from "../../hooks/useResendPayeeInvite";

/**
 * The payee facts the header/row actions need, normalized by each caller from
 * whatever it holds: the v3 `Payee` (contractor detail hub) or the search-service
 * `IPayeeRow` (contractors list). Keeping the actions on this small shape decouples
 * them from either fat type — the hub reads v3 (`status`/`linkRequestStatus`/`id`/
 * `internalNotes`/`identitySource`), the list keeps reading `IPayeeRow`.
 */
export type PayeeRowActionsData = {
  payeeId: string;
  email?: string;
  internalNotes?: string;
  isInactive: boolean;
  isPending: boolean;
  isPayeeProvidedInfo: boolean;
};

export const usePayeeRowActions = () => {
  const { openSnackbar } = useWSSnackbar();
  const history = useHistory();
  const modalAddEngagement = useCreatePayeeEngagementModal();
  const modalEditContractor = useEditContractorModal();
  const [restorePayee] = queriesPayees.activatePayee.useMutation();
  const modalInternalNotes = useInternalNotesModal();
  const modalArchiveContractor = useArchiveContractorModal();
  const organizationId = useXWingspanPayerId();
  const isOrgContext = !!organizationId;
  const goTo = useGoTo();
  const { hasScope } = useAccountAccess();
  const [resendInvite, resendInviteMeta] = useResendPayeeInvite();

  return ({
    payee,
    backPath
  }: {
    payee: PayeeRowActionsData;
    backPath: string;
  }) => {
    const isInactive = payee.isInactive;

    const headerActions: WSPageToolbarAction[] = [
      ...(isInactive || !hasScope("payments.payable")
        ? []
        : ([
            {
              label: "Create payable",
              icon: "plus",
              disabled: isOrgContext,
              buttonKind: payee.isPending ? undefined : "Primary",
              onClick: () => {
                history.push("/member/invoices/payables/create", {
                  backPath,
                  email: payee.email
                });
              }
            }
          ] as const)),
      ...(isInactive
        ? ([
            {
              label: "Restore",
              onAsyncClick: async () => {
                await restorePayee(
                  {
                    payeeId: payee.payeeId,
                    xWingspanAccount: organizationId
                  },
                  {
                    onSuccess: () => {
                      openSnackbar({
                        duration: 5000,
                        type: "success",
                        message: "Contractor restored successfully"
                      });
                    },
                    onError: () => {
                      openSnackbar({
                        duration: 5000,
                        type: "warning",
                        message: `Failed to restore contractor`
                      });
                    }
                  }
                );
              },
              icon: "repeat-circle"
            }
          ] as const)
        : ([
            ...(payee.isPending
              ? [
                  {
                    label: "Resend invite",
                    name: "resendInvite",
                    buttonKind: "Primary",
                    onAsyncClick: async () => {
                      await resendInvite(payee.payeeId);
                    },
                    icon: "send"
                  }
                ]
              : []),
            ...(payee.isPayeeProvidedInfo
              ? []
              : [
                  {
                    label: "Edit contractor",
                    onClick: () => {
                      goTo(buildPayeeEditTaxInfoPath(payee.payeeId));

                      // modalEditContractor.open({
                      //   payee: queryPayeeData
                      // });
                    },
                    icon: "edit"
                  }
                ]),
            {
              label: "Archive contractor",
              onClick: () => {
                modalArchiveContractor.open({
                  payeeId: payee.payeeId
                });
              },
              icon: "archive"
            },
            {
              label: "Edit internal notes",
              onClick: () => {
                modalInternalNotes.open({
                  payeeId: payee.payeeId,
                  internalNotes: payee.internalNotes
                });
              },
              icon: "edit"
            },
            // {
            //   label: "Delete contractor",
            //   onClick: () => {
            //     history.push(`${match.url}/delete`);
            //   },
            //   destructive: true,
            //   icon: "trash"
            // },
            {
              label: "Add assignments",
              onClick: () => {
                modalAddEngagement.open({
                  payeeId: payee.payeeId
                });
              },
              icon: "plus"
            }
          ] as WSPageToolbarAction[]))
    ];

    return headerActions;
  };
};
